import { useState } from "react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/tooltip";

import { cn } from "@/lib/utils";

const TOOLTIP_COPY =
  "Last month's top revenue earner wears the crown. Turn this on to wear it only on the leaderboard and keep your own topper everywhere else.";

// Compact "leaderboard only" toggle for the crown toolbar. Pings until noticed.
export const ShopCrownToggle = ({
  isLeaderboardOnly,
  onLeaderboardOnlyChange,
}: {
  isLeaderboardOnly: boolean;
  onLeaderboardOnlyChange: (isLeaderboardOnly: boolean) => void;
}) => {
  /* React Hooks */
  const [hasNoticed, setHasNoticed] = useState(false);

  /* Handlers */
  const handleNotice = () => {
    setHasNoticed(true);
  };

  const handleToggle = () => {
    setHasNoticed(true);
    onLeaderboardOnlyChange(!isLeaderboardOnly);
  };

  /* Render */
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Row
            className="relative items-center gap-1.5"
            onMouseEnter={handleNotice}
          >
            {/* Caps-only label + the toggle's hard shadow read high; nudge to the visual center. */}
            <Text className="translate-y-px font-mono text-[8px] font-black uppercase leading-none tracking-[0.1em] text-slate-500">
              LB only
            </Text>
            {/* Pixel toggle: square knob slides right and the track goes amber. */}
            <Button
              type="button"
              variant="outlineDark"
              onClick={handleToggle}
              className={cn(
                "relative h-4 w-7 rounded-none border-2 border-slate-900 p-0 shadow-[2px_2px_0_#172033]",
                isLeaderboardOnly
                  ? "bg-amber-300 hover:bg-amber-400"
                  : "bg-slate-200 hover:bg-slate-300",
              )}
            >
              <Column
                className={cn(
                  "absolute top-0 h-full w-2.5 border-slate-900 bg-white transition-[left]",
                  isLeaderboardOnly
                    ? "left-[calc(100%-0.625rem)] border-l-2"
                    : "left-0 border-r-2",
                )}
              />
            </Button>
            {hasNoticed ? null : (
              <Column className="pointer-events-none absolute -right-2 -top-2 h-2.5 w-2.5">
                <Column className="absolute inset-0 animate-ping rounded-full bg-amber-500 opacity-75" />
                <Column className="relative h-2.5 w-2.5 rounded-full border border-slate-900 bg-amber-400" />
              </Column>
            )}
          </Row>
        </TooltipTrigger>
        <TooltipContent className="max-w-56 rounded-none border-2 border-slate-900 bg-[#fffaf0] font-mono text-[10px] font-bold normal-case text-slate-900">
          {TOOLTIP_COPY}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
